/* eslint-disable no-underscore-dangle */

import sortTypes from "sheets/sort-types"
import moment from "moment"
import Immutable from "immutable"
import { dayMonthYearToMoment } from "shared/imports/sharedHelperFunctions"
import {
    filterCurrencyFn,
    filterRowByStringColumn,
    filterRowByBooleanCustomFieldColumn,
    filterRowByCustomFieldColumn,
    nullTypes,
    sortCurrencyFn,
    handleNullTypes,
    stringSort,
    billNumberSort,
    congressionalDistrictSort,
    dateSort,
    getPrimaryAssigneeFullName,
    getValuesFromJSONData,
} from "sheets/helper-functions"

/*
Given quorum data type, what is the key of the object we should
compare against a search string to filter the rows of
a table
 */
export const rowSearchKeyForDataType = (dataType) => DjangIO.app.models.QuorumDataType.by_value(dataType).display_key

/**
 * Decide whether we should include the provided date as a filter to the backend
 * We want to make sure only valid dates get send to the backend
 * @param {Object|null} dateObj - A date object
 * @param {String} additionalKey - Either '__gte' or __ '__lte'
 * @param {String} key - the filter key for the column
 *
 * @returns {Object} - Either a formatted filter object or an empty object
 */
export const handleDateFilter = (dateObj, key, additionalKey = "gte") => {
    if (!key || !dateObj || !dateObj.year || !dateObj.month || !dateObj.day) {
        return {}
    } else {
        return { [`${key}__${additionalKey}`]: `${dateObj.year}-${dateObj.month}-${dateObj.day}` }
    }
}

/*
Given the field object from the DjangIO, and a key and value
to filter with (assumed immutable when not a single value)
return the correct filter dictionary
 */
export const filterForResourceField = (field, key, value, overrideFilterKey = "") => {
    // if we passed in an override key through the column config system, use it
    if (overrideFilterKey) {
        return { [`${key}__${overrideFilterKey}`]: value }
    }

    // if we were unable to find a field through the DjangIO system or we are filtering
    // for an enum
    if (!field || field._is_enum_field) {
        // check to see if the enum search value happens to not be an Array
        if (value.constructor !== Array) {
            return { [key]: value }
        }
        // otherwise, Enum search values are passed as Arrays
        return { [`${key}__in`]: value }
    }

    switch (field.field_type) {
        case DjangIO.djangio.types.FieldType.CharField.value:
        case DjangIO.djangio.types.FieldType.ForeignKey.value:
        case DjangIO.djangio.types.FieldType.TextField.value:
            return { [`${key}__icontains`]: value }
        case DjangIO.djangio.types.FieldType.IntegerField.value:
            const [min, max] = value
            return { [`${key}__gte`]: min, [`${key}__lte`]: max }
        // in this case of dates, we will have a startDate and an endDate
        case DjangIO.djangio.types.FieldType.DateTimeField.value:
        case DjangIO.djangio.types.FieldType.QuorumDateTimeField.value:
        case DjangIO.djangio.types.FieldType.DateField.value:
            const { startDate, endDate } = value
            return Object.assign(handleDateFilter(startDate, key), handleDateFilter(endDate, key, "lte"))
        default:
            return { [key]: value }
    }
}

/**
 * Filter a date by startDate and endDate bounds
 * Return true if the date meets startDate <= dateValue <= endDate
 * If startDate does not exist, there is no startDate boundary
 * If endDate does not exist, there is no endDate boundary
 * @param {String} dateValue - the date in question, of the form "2018-02-09T17:00:00" - serialized from the backend
 * @param {Immutable} startDate - the lower boundary of the filter
 * @param {Immutable} endDate - the upper boundary of the filter
 * @returns {Boolean} - Whether or not the value meets the filtering criteria
 */
export const dateFilter = (dateValue, startDate, endDate) => {
    // if the value is not truthy, then we should filter it out completely
    if (!dateValue) {
        return false
    }

    // add filtering conditions, and test at the end if they are all true
    let filteringConditions = []

    // use the helper function to convert the date to moment
    const lowerBoundary = dayMonthYearToMoment(startDate && startDate.toJS ? startDate.toJS() : startDate)
    const upperBoundary = dayMonthYearToMoment(endDate && endDate.toJS ? endDate.toJS() : endDate)
    if (lowerBoundary) {
        filteringConditions.push(moment(dateValue) >= lowerBoundary)
    }
    if (upperBoundary) {
        filteringConditions.push(moment(dateValue) <= upperBoundary)
    }
    return filteringConditions.all()
}

/*
Given a column data type and a value to search on, return a function that
filters values to match that value
*/
/**
 * This function returns a function that is used to to determine whether a row should be filtered out when a filter
 * is applied to a particular column
 * @name filterFunctionForColumnDataType
 * @function
 * @param {Integer|String|Object|?} searchingColumnValue - represents the value used to filter a column. This is the value(s)
 * that is selected by the user when filtering the column
 * @param {Integer} columnDataType - member of the ColumnDataType enum that represents what type of data the column displays
 * @param {Integer} columnType - member of the SheetColumnType enum that represents what kind of column this function is for
 * @param {String} columnField - a string representing the field on the model (or related model) of the sheet's data type
 * @param {Integer} sheetDataType - a member of QuorumDataType, representing the type of Sheet - Person, Bill, etc
 * @param {Integer/Undefined} tagType - a member of TagType enum represents what type of data the custom field column display.
 * Undefined for data type other than custom-tag
 *
 * @returns {Function} - A function that must take in the columnValue of type (String|Integer|Object|List|Map), and optionally any of the
 * variables within the scope of filterFunctionForColumnDataType, and returns a Boolean
 */
export const filterFunctionForColumnDataType = (
    searchingColumnValue,
    columnDataType,
    columnType,
    columnField,
    sheetDataType,
    tagType,
) => {
    let filter

    if (columnDataType && columnDataType === DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.year.value) {
        const [min, max] = searchingColumnValue.toJS()
        return (value) => {
            const yearInt = Number(moment(value).local().format("YYYY"))
            return value != null && yearInt >= Number(min) && yearInt <= Number(max)
        }
    }

    switch (columnType) {
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.staffer.value:
            filter = filterFunctionForColumnDataType(
                searchingColumnValue,
                DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.string.value,
            )
            return (value) => filter(value && value.get("name"))
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.stakeholder_alignment.value:
            return (value) => searchingColumnValue === value
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.regulations_assignment.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.issue_assignment.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment_primary.value:
            // we want to search the names of all the assigned users
            filter = filterFunctionForColumnDataType(
                searchingColumnValue,
                DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.string,
            )
            return (value) =>
                filter(
                    value &&
                        // join all the names together
                        value
                            .map((assignment) =>
                                [assignment.get("recipient__first_name"), assignment.get("recipient__last_name")].join(
                                    " ",
                                ),
                            )
                            .join(" "),
                )
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_county_legislator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_representative.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_representative.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_senator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_assemblymember.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_delegate.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_senator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_municipal_legislator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_councilmember.value:
            return (value) => {
                const lowerValue = searchingColumnValue.toLowerCase()
                for (let person of value) {
                    const lowerName = person.get("name").toLowerCase()
                    if (lowerName.indexOf(lowerValue) >= 0) {
                        return true
                    }
                }
                return false
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_field_from_note.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_tag.value:
            if (tagType === DjangIO.app.person.types.TagType.date.value) {
                const startDate = searchingColumnValue.get("startDate")
                const endDate = searchingColumnValue.get("endDate")

                // returns true if the date is within the range, and returns if false if not or if there is no date available
                return (value) => dateFilter(value, startDate, endDate)
            } else if (tagType === DjangIO.app.person.types.TagType.boolean.value) {
                return (value) =>
                    filterRowByBooleanCustomFieldColumn({ searchingColumnValue, columnValue: value, tagType })
            } else if (tagType === DjangIO.app.person.types.TagType.string.value) {
                return (value) => filterRowByStringColumn({ searchingColumnValue, columnValue: value })
            } else {
                return (value) => filterRowByCustomFieldColumn({ searchingColumnValue, columnValue: value, tagType })
            }

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.percentage_of_columns.value:
            const min = searchingColumnValue.get(0)
            const max = searchingColumnValue.get(1)
            return (value) =>
                value * 100 != null && Number(value * 100) >= Number(min) && Number(value * 100) <= Number(max)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.field.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.staffer_field.value:
            // see if we can find the field
            const field = DjangIO.findField(columnField, sheetDataType)

            // if we do
            if (field) {
                // and it's not an enum field
                if (!field._is_enum_field) {
                    switch (field.field_type) {
                        case DjangIO.djangio.types.FieldType.IntegerField.value:
                            return filterFunctionForColumnDataType(
                                searchingColumnValue,
                                DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value,
                            )
                        case DjangIO.djangio.types.FieldType.DateTimeField.value:
                        case DjangIO.djangio.types.FieldType.QuorumDateTimeField.value:
                        case DjangIO.djangio.types.FieldType.DateField.value:
                            return filterFunctionForColumnDataType(
                                searchingColumnValue,
                                DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.date.value,
                            )
                        case DjangIO.djangio.types.FieldType.BooleanField.value:
                        case DjangIO.djangio.types.FieldType.NullBooleanField.value:
                            return filterFunctionForColumnDataType(
                                searchingColumnValue,
                                DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.boolean.value,
                            )
                    }
                }
            }
    }

    switch (columnDataType) {
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.boolean.value:
            return (value) => Boolean(value) === Boolean(searchingColumnValue)
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value:
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value:
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.long.value:
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.percent.value:
            const min = searchingColumnValue.get(0)
            const max = searchingColumnValue.get(1)
            return (value) => value != null && Number(value) >= Number(min) && Number(value) <= Number(max)
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.currency.value:
            const minimum = searchingColumnValue.get(0)
            const maximum = searchingColumnValue.get(1)
            return (value) =>
                filterCurrencyFn({
                    maximum,
                    minimum,
                    value,
                })
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.date.value:
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.year.value:
            const startDate = searchingColumnValue.get("startDate")
            const endDate = searchingColumnValue.get("endDate")

            // returns true if the date is within the range, and returns if false if not or if there is no date available
            return (value) => dateFilter(value, startDate, endDate)

        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.string.value:
            return (value) => filterRowByStringColumn({ searchingColumnValue, columnValue: value })

        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.enum_value.value:
        default:
            return (value) => {
                const comparison = (compareTo) => {
                    if (value && value.toJS) {
                        // don't have to lowerCase or check isNaN; if value is an array, this is a selectMany cell (many options are the value of the cell)
                        // the values being set (choose many can't be a text input cell), we can compare them directly to the filter selected
                        return value.toJS().includes(compareTo)
                    }
                    if (isNaN(compareTo)) {
                        return value && value.toLowerCase().includes(compareTo.toLowerCase())
                    }
                    return parseInt(compareTo) === parseInt(value)
                }

                if (typeof searchingColumnValue === "object") {
                    for (let colVal of searchingColumnValue.toJS()) {
                        if (comparison(colVal)) {
                            return true
                        }
                    }
                    return false
                }

                return comparison(searchingColumnValue)
            }
    }
}

/*
Given a column data type and a search type, return a function that sorts
a list of rows in that direction.
 */
/**
 * This function returns a function that is used to to sorts rows of column
 * @name sortFunctionForColumnDataType
 * @function
 * @param {Integer} sortTypes - member of sheetSortType enum that represents sorting direction (ascending/descending)
 * that is selected by the user when sorting the column
 * @param {Integer} columnDataType - member of the ColumnDataType enum that represents what type of data the column displays
 * @param {Integer} columnType - member of the SheetColumnType enum that represents what kind of column this function is for
 * @param {String} columnField - a string representing the field on the model (or related model) of the sheet's data type
 * @param {Integer} sheetDataType - a member of QuorumDataType, representing the type of Sheet - Person, Bill, etc
 * @param {Integer/Undefined} tagType - a member of TagType enum represents what type of data the custom field column display.
 * Undefined for data type other than custom-tag
 * @returns {Function} - A comparison function for rows of column
 */
export const sortFunctionForColumnDataType = (
    sortType = sortTypes.DESC,
    columnDataType,
    columnType,
    sheetDataType,
    columnField = undefined,
    tagType,
) => {
    const sortDescending = sortType === sortTypes.DESC
    let sortByString
    let integerSort
    let getBillParts

    switch (columnType) {
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.member_birth_month_day.value:
            // only consider the month and day when sorting this column
            // assuming a and b look like: a = "1962-02-11", b = "1962-03-30"
            const getMonthDay = (yearMonthDay) => {
                try {
                    return yearMonthDay
                        .split("-")
                        .filter((_, index) => index !== 0)
                        .join("/")
                } catch (error) {
                    console.warn(`${yearMonthDay} is poorly formatted`)
                    return null
                }
            }
            sortByString = sortFunctionForColumnDataType(
                sortType,
                DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.string.value,
            )
            return (a, b) => sortByString(a && getMonthDay(a), b && getMonthDay(b))

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.staffer.value:
            sortByString = sortFunctionForColumnDataType(
                sortType,
                DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.string.value,
            )
            return (a, b) => sortByString(a && a.get("name"), b && b.get("name"))

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.regulations_assignment.value:
            // if it's an integer column, sort by the number of people assigned
            integerSort = sortFunctionForColumnDataType(
                sortType,
                DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value,
            )
            return (a, b) => integerSort(a && a.size, b && b.size)
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.assignment_primary.value:
            return (a, b) => {
                sortByString = sortFunctionForColumnDataType(
                    sortType,
                    DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.string.value,
                )
                return sortByString(getPrimaryAssigneeFullName(a), getPrimaryAssigneeFullName(b))
            }

        // sort by the definitions of the committee membership
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.committee_membership.value:
            // We'll sort primarily by the order field
            // lookup the enum value using by_value, since we're only passed that enum
            // we're checking the order field of the enum and passing that value, or if it doesn't have one
            // we'll pass undefined
            integerSort = sortFunctionForColumnDataType(
                sortType,
                DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value,
            )
            return (a, b) => {
                const roleEnum = DjangIO.app.committee.models.CommitteeMemberRole

                //If enum value has an explicit ordering, apply that.  If enum value exists but has no explicit ordering,
                //put it after those (Infinity), and if enum doesn't exist (i.e. this row is not a committee member,)
                //put it after those (undefined)
                const a_order = a ? (roleEnum.by_value(a).order ? roleEnum.by_value(a).order : Infinity) : undefined
                const b_order = b ? (roleEnum.by_value(b).order ? roleEnum.by_value(b).order : Infinity) : undefined
                return integerSort(a_order, b_order)
            }
        // hearing date rows could have mutiple dates for a cell separated by a comma, so sort the column by the
        // first date within each string, which will be the future-most date
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.future_hearing_date.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.hearing_date.value:
            return (a, b) => {
                const a_date = a && new Date(a.split(", ")[0])
                const b_date = b && new Date(b.split(", ")[0])
                return sortDescending ? dateSort({ shouldReverse: true })(a_date, b_date) : dateSort({})(a_date, b_date)
            }
        // for Region Abbreviation, sort by the Abbreviation
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.region_abbrev_bill.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.region_abbrev_person.value:
            // We'll sort primarily by the order field
            // lookup the enum value using by_value, since we're only passed that enum
            // we're checking the order field of the enum and passing that value, or if it doesn't have one
            // we'll pass undefined
            return (a, b) => {
                const a_order = a ? DjangIO.app.models.Region.by_value(a).region_name : undefined
                const b_order = b ? DjangIO.app.models.Region.by_value(b).region_name : undefined
                return sortDescending
                    ? stringSort({ shouldReverse: true })(a_order, b_order)
                    : stringSort({})(a_order, b_order)
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_public_organization.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.last_interaction_type_office.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.note_interaction_type.value:
            integerSort = sortFunctionForColumnDataType(
                sortType,
                DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value,
            )
            return (a, b) => {
                const a_order = a ? DjangIO.app.userdata.types.InteractionType.by_value(a).order : undefined
                const b_order = b ? DjangIO.app.userdata.types.InteractionType.by_value(b).order : undefined
                return integerSort(a_order, b_order)
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.sponsorship_record.value:
            integerSort = sortFunctionForColumnDataType(
                sortType,
                DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value,
            )
            return (a, b) => {
                return integerSort(a, b)
            }

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_county_legislator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_representative.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_representative.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_senator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_assemblymember.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_delegate.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_senator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_municipal_legislator.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_data_person_state_councilmember.value:
            // We just sort by the first value
            return (a, b) => {
                const a_name = a.has(0) && a.get(0).get("name")
                const b_name = b.has(0) && b.get(0).get("name")
                return sortDescending
                    ? stringSort({ shouldReverse: true })(a_name, b_name)
                    : stringSort({})(a_name, b_name)
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_field_from_note.value:
            return (a, b) => {
                const a_date = typeof a === "object" ? a?.get("value") : a
                const b_date = typeof b === "object" ? b?.get("value") : b
                return sortDescending ? dateSort({ shouldReverse: true })(a_date, b_date) : dateSort({})(a_date, b_date)
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.custom_tag.value:
            if (tagType === DjangIO.app.person.types.TagType.date.value) {
                return sortDescending ? dateSort({ shouldReverse: true }) : dateSort({})
            }
            break
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.state_abbrev_bill_label_column.value:
            /* Emulate bill_alphanumeric_ascending/descending here
                Sort by:
                    1) Region
                    2) BillType.full_name
                    3) Number
                in that order. There are more parts to bill_alphanumeric sorting, but this is all we have
                for the front end at this point in time.

                NOTE: BillType.full_name sorts by full_name value on the backend but we only have
                custom labels (H.R., S., S.R.), which are very close to identical alphabetically,
                but there may be an edge case in the future. In such a case, we may need to re-evaluate
                the content we pass to the cell, and add custom rendering to display cell content correctly
                while maintaining enough info for a truly-symmetric sort compared to the backend.
            */

            getBillParts = (data) => {
                // "GA H.B.1060" => [,"GA", "H.B.e", "1060"] - (region) (label) (number)
                const match = data.match(/^(\w+)\b\s(.+\b)\s?(\d+$)/)
                if (match) {
                    const [_bill, region, label, number] = match
                    return [region, label, Number(number)]
                }
                return []
            }
            return (a, b) => {
                const aBillParts = getBillParts(a)
                const bBillParts = getBillParts(b)
                // some bills have irregularly formed labels... (companion bills)
                if (!aBillParts.length || !bBillParts.length) {
                    // Sorting by string alphabetically will put any non-standard bills up top within their identical
                    // region + label groupings, so this is pretty dang close to the original sorting, but numbers may
                    // be off. It's not a great solution, but the alternative is too costly given the upcoming Sheets
                    // overhaul epics.
                    return stringSort({ shouldReverse: sortDescending })(a, b)
                }
                const [aRegion, aLabel, aNumber] = aBillParts
                const [bRegion, bLabel, bNumber] = bBillParts
                // otherwise, we're good to compare bill part elements
                integerSort = sortFunctionForColumnDataType(
                    sortType,
                    DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value,
                )
                let comparisonResult = stringSort({ shouldReverse: sortDescending })(aRegion, bRegion)
                if (comparisonResult !== 0) {
                    return comparisonResult
                }
                comparisonResult = stringSort({ shouldReverse: sortDescending })(aLabel, bLabel)
                if (comparisonResult !== 0) {
                    return comparisonResult
                }
                // Return opposite sort to match A-Z, low-to-high
                return -1 * integerSort(aNumber, bNumber)
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.supporter_charity_additional_info.value:
            return (a, b) => {
                // always put Not Editable value at the bottom
                const isANotEditable = a === "Not Editable"
                const isBNotEditable = b === "Not Editable"
                if (isANotEditable && !isBNotEditable) {
                    return 1
                }
                if (!isANotEditable && isBNotEditable) {
                    return -1
                }
                if (isANotEditable && isBNotEditable) {
                    return 0
                }
                return sortDescending ? stringSort({ shouldReverse: true })(a, b) : stringSort({})(a, b)
            }
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.supporter_charity_anonymous.value:
            return (a, b) => {
                // always put Not Editable value at the bottom
                const isANotEditable = a === "Not Editable"
                const isBNotEditable = b === "Not Editable"
                if (isANotEditable && !isBNotEditable) {
                    return 1
                }
                if (!isANotEditable && isBNotEditable) {
                    return -1
                }
                if (isANotEditable && isBNotEditable) {
                    return 0
                }

                // Prevents flipping of values by returning 1 when the values match (keeps 'em in order)
                if (!!a === !!b) {
                    return 0
                }
                if (sortDescending) {
                    return a && !b ? -1 : 1
                }
                return !a && b ? -1 : 1
            }
        // break out of this switch statement and move on to try to match on column data type
        default:
            break
    }

    switch (columnDataType) {
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.decimal.value:
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.enum_value.value:
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.integer.value:
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.long.value:
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.percent.value:
            // returns 1 if a is larger than b, -1 if a is smaller than b, or 0 if they are equal
            // according to the comparator requirements by ImmutableJS
            return (a, b) => {
                // if neither are null
                if (!nullTypes.includes(a) && !nullTypes.includes(b)) {
                    return sortDescending ? Number(b) - Number(a) : Number(a) - Number(b)
                } else {
                    // at least 1 is in nullType
                    return handleNullTypes(a, b)
                }
            }

        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.currency.value:
            return sortDescending ? sortCurrencyFn({ shouldReverse: true }) : sortCurrencyFn({})

        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.date.value:
            return sortDescending ? dateSort({ shouldReverse: true }) : dateSort({})

        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.field.value:
            // depending on the data type of the field, we need to handle sorting differently
            // see if we can find the field
            try {
                const fieldObj = DjangIO.findField(columnField, sheetDataType)
                if (fieldObj && fieldObj._is_enum_field) {
                    const enumObj = DjangIO.enumFromField(fieldObj)
                    const attr = fieldObj.enum_sorting_attr || "label"
                    // make sure that if the value is null or empty we don't get errors
                    return (e_1, e_2) => {
                        const label_1 = enumObj.by_value(e_1) ? enumObj.by_value(e_1)[attr] : null
                        const label_2 = enumObj.by_value(e_2) ? enumObj.by_value(e_2)[attr] : null
                        return sortDescending
                            ? stringSort({ shouldReverse: true })(label_1, label_2)
                            : stringSort({})(label_1, label_2)
                    }
                } else if (
                    sheetDataType === DjangIO.app.userdata.spreadsheet.types.SheetDataType.bill.value &&
                    fieldObj &&
                    fieldObj.name === "label"
                ) {
                    return sortDescending ? billNumberSort({ shouldReverse: true }) : billNumberSort({})
                } else if (
                    sheetDataType === DjangIO.app.userdata.spreadsheet.types.SheetDataType.person.value &&
                    fieldObj &&
                    fieldObj.verbose_name === "Congressional District"
                ) {
                    return sortDescending
                        ? congressionalDistrictSort({ shouldReverse: true })
                        : congressionalDistrictSort({})
                } else {
                    return sortDescending ? stringSort({ shouldReverse: true }) : stringSort({})
                }
            } catch (error) {
                return sortDescending ? stringSort({ shouldReverse: true }) : stringSort({})
            }

        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.boolean.value:
            return (a, b) => {
                // Prevents flipping of values by returning 1 when the values match (keeps 'em in order)
                if (!!a === !!b) {
                    return 0
                }
                if (sortDescending) {
                    return a && !b ? -1 : 1
                }
                return !a && b ? -1 : 1
            }
        case DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.string.value:
        default:
            return sortDescending ? stringSort({ shouldReverse: true }) : stringSort({})
    }
}
/**
 * Perform the requested mathematical operation on the requested columns by merging n Maps of key/value pairs,
 * where keys represent a row entity, and the value represents
 * the number in the cell corresponding to that row in a given column
 * @param {List} columns - a list of the columns to merge together
 * @param {Function} operator - a function to operate on two numbers of type (float, float) => float,
 * @param {Number} defaultOperand - a defaultValue to use when only 1 operand is available
 * @param {Boolean} divMul - a flag for multiplication / division, needed for reducing the values down

 * @returns {Immutable.Map} a map of the merged data
 */
const performMath = (columns, operator, divMul = false, defaultOperand = 0) => {
    // for some reason, Immutable returns undefined for a Map with an empty key 'data'
    // if columns contains 1 key that is undefined, we have nothing to do
    if (!columns || columns.size === 0 || !columns.get(0)) {
        return Immutable.fromJS({})
    }
    // we need this to properly evaluate an array of multiplication or division columns. If any
    // array contains less values than the number of columns, we should default to 0 for that key
    // this is consistent with the way excel would compute values
    const numColumns = columns.size

    // build up an object of k/v pairs where each value is an array of the cells corresponding to the key
    const collectedValues = columns.reduce(
        (acc, currentColumn) => {
            if (!currentColumn) {
                return acc
            }
            // itterate over all of the children in currentColumn, building up an array of values for each key
            // after we build up the array of values, we can apply the operator on each of them
            Object.entries(currentColumn.toJS()).forEach(([key, value]) => {
                // must filter out null values. Number(value) if value == null will return 0, and then
                // performMath will render 0 on seemingly empty cells
                if (value !== null) {
                    // if the key is already in the accumulator, just append
                    if (key in acc) {
                        acc[key].push(Number(value))
                        // if there is no key, then we need to make sure we append a 0 to the beginning
                        // of the array for the corresponding key, for subtraction to work properly
                    } else {
                        acc[key] = [defaultOperand, Number(value)]
                    }
                }
            })
            return acc // the return value of the function passed to reduce should be the accumulator
        },
        // the second argument to reduce is the initial accumulator
        // we need to create a new object from the keys of the first column, where the values
        // correspond to empty arrays to be filled
        Object.keys(columns.first().toJS()).reduce((acc, currKey) => {
            acc[currKey] = []
            return acc
        }, {}),
    )
    const mergedValues = {}
    Object.entries(collectedValues).forEach(([key, values]) => {
        // if there is no data, set mergedValues of the key to be null
        // this case is hit if there is currently no data (cell values are null)
        if (values.length === 0) {
            mergedValues[key] = null
        }
        // if we are multiplying / dividing and:
        //      - the number of values doesn't match the number of columns
        //      - we are multiplying / dividing 1 column
        // we should return 0 be default
        else if ((divMul && values.length !== numColumns) || (divMul && numColumns === 1)) {
            mergedValues[key] = 0
        }
        // if it's empty, we can't call reduce on it without a defualt accumulator
        // there is no data, so there is nothing to do
        else if (!values.length) {
            // no body here
        }
        // otherwise, process the values with the operator
        else {
            mergedValues[key] = values.reduce(operator)
        }
    })
    // convert to ImmutableMap and return the merged values
    return Immutable.fromJS(mergedValues)
}

/**
 * This recursive function computes the data for the sheet. For non-derived cells, the column
 * data is taken directly from the data field on the column.
 * If a field is a derived column, it relies on other columns' data to compute its own data
 * The data that it relies on may also derived, so we need to make sure that it is computed as well before
 * we use it for our own computation
 * @param {Map} column a column in the sheet
 * @param {List} a list of columns in the sheet

 * @returns {Immutable.Map} the data, in the form of a Map of k/v pairs where the key is the entity id
 * and the value
 */
export const computeColumnData = (column, columns, isPartOfMathRecurrence = false) => {
    const columnType = column.get("column_type")
    const containsNonScalarValues =
        columnType &&
        DjangIO.app.userdata.spreadsheet.types.SheetColumnType.by_value(columnType).generates_non_scalar_values

    // An attribute set on a SheetColumnType if it does not generate primitive types but instead an object with a cell_value property containing
    // the value to render.
    if (containsNonScalarValues) {
        return getValuesFromJSONData(column)
    }

    // switch on the column type
    switch (column.get("column_type")) {
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.staffer_field.value:
            return (
                columns
                    // find the parent column
                    .find((col) => col.get("id") === column.get("parent_column_id"))
                    // get its data
                    .get("data")
                    // extract this column's field from it
                    .map((staffer) => staffer.get(column.get("field")))
            )

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.sum_of_columns.value:
            // call performMath on the result of the filter, includes, and map
            return performMath(
                columns
                    // filter to the columns that this column points to
                    .filter((col) =>
                        column
                            .get("source_columns")
                            .includes(DjangIO.app.userdata.spreadsheet.models.Column.foreignKey(col.get("id"))),
                    )
                    // recursively call computeColumnData on the children
                    .map((col) => computeColumnData(col, columns, true)),
                (firstVal, secondVal) => firstVal + secondVal,
            )

        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.multiplication_of_columns.value:
            // call performMath on the result of the filter, includes, and map
            return performMath(
                columns
                    // filter to the columns that this column points to
                    .filter((col) =>
                        column
                            .get("source_columns")
                            .includes(DjangIO.app.userdata.spreadsheet.models.Column.foreignKey(col.get("id"))),
                    )
                    // recursively call computeColumnData on the children
                    .map((col) => computeColumnData(col, columns, true)),
                (firstVal, secondVal) => firstVal * secondVal,
                true, // make sure we flag that it is multiplication
            )

        // if it's a subtraction column, subtract the two columns
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.difference_of_columns.value:
            let childColumns = Immutable.fromJS([])
            const first_column = columns.filter(
                (col) =>
                    column.get("first_column") ===
                    DjangIO.app.userdata.spreadsheet.models.Column.foreignKey(col.get("id")),
            )
            // if the first column doesn't exist, then we need to pass in empty object so that performMath understands
            // to use the right sign for the subtraction
            if (first_column.size) {
                childColumns = childColumns.concat(first_column)
            } else {
                childColumns = childColumns.concat(Immutable.fromJS({ data: {} })) // column with empty data
            }

            // just concatenate the second column
            childColumns = childColumns.concat(
                columns.filter(
                    (col) =>
                        column.get("second_column") ===
                        DjangIO.app.userdata.spreadsheet.models.Column.foreignKey(col.get("id")),
                ),
            )

            // after computing the column data for the children, merge the data with the provided subtraction function
            return performMath(
                childColumns.map((col) => computeColumnData(col, columns, true)),
                (firstVal, secondVal) => firstVal - secondVal,
            )

        // if it's a division column, subtract the two columns
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.percentage_of_columns.value:
        case DjangIO.app.userdata.spreadsheet.types.SheetColumnType.division_of_columns.value:
            let divisionChildColumns = Immutable.fromJS([])
            divisionChildColumns = divisionChildColumns.concat(
                columns.filter(
                    (col) =>
                        column.get("first_column") ===
                        DjangIO.app.userdata.spreadsheet.models.Column.foreignKey(col.get("id")),
                ),
            )
            divisionChildColumns = divisionChildColumns.concat(
                columns.filter(
                    (col) =>
                        column.get("second_column") ===
                        DjangIO.app.userdata.spreadsheet.models.Column.foreignKey(col.get("id")),
                ),
            )
            // after computing the column data for the children, merge the data with the provided division function
            return performMath(
                divisionChildColumns.map((col) => computeColumnData(col, columns, true)),
                (firstVal, secondVal) => {
                    // 0/0 returns NaN in JavaScript, but this messes with sorting sheet columns. Instead, we return Infinity
                    // and have the sheet treat 0/0 the same as something like 5/0. This results in 0/0 and 5 / 0 sorted
                    // in a similar fashion
                    if (secondVal === 0 && firstVal === 0) {
                        return Infinity
                    }
                    return firstVal / secondVal
                },
                true, // make sure we flag that it is division
            )
        // get the data from the column. This serves as the base case for this recursive function
        default:
            // Only call this special behavior if the column is a currency column, in order to multiply/divide dollars instead of cents.
            // This is because e.g. multiplying 4200 * 4200 is much different than 42.00 * 42.00.
            if (
                !column.isEmpty() &&
                isPartOfMathRecurrence &&
                column.get("column_data_type") ===
                    DjangIO.app.userdata.spreadsheet.data_types.ColumnDataType.currency.value
            ) {
                // Want to convert cents str (e.g. 4242) to expected value (e.g. 42.42).
                // Explicitly cast to float as well to preserve decimal places.
                return column.get("data").map(
                    (item) => (parseInt(item) / 100).toFixed(2), // Everything else is returned as a string, so stay consistent. "42.42"
                )
            }

            // we also check to make sure if column isn't empty
            return column.isEmpty() ? Immutable.fromJS({}) : column.get("data")
    }
}

export const customColumnsForDataType = (dataType) => {
    const items = DjangIO.app.userdata.spreadsheet.types.SheetColumnType.items().filter(
        (item) => item.category === DjangIO.app.userdata.spreadsheet.types.ColumnCategory.custom.value,
    )

    switch (dataType) {
        case DjangIO.app.models.QuorumDataType.person.value:
        case DjangIO.app.models.QuorumDataType.staffer.value:
            // exclude custom tags for people
            return items.filter((item) => !["custom_tag"].includes(item.key))

        case DjangIO.app.models.QuorumDataType.supporter.value:
            // exclude scorecards and custom
            return items.filter((item) => !["scorecard", "custom_data", "scorecard_item"].includes(item.key))
        case DjangIO.app.models.QuorumDataType.public_organization.value:
            // exclude scorecards and custom and assignment
            return items.filter(
                (item) => !["scorecard", "custom_data", "scorecard_item", "assignment"].includes(item.key),
            )

        case DjangIO.app.models.QuorumDataType.project.value:
            // exclue lists and assignments
            return items.filter(
                (item) =>
                    ![
                        "scorecard",
                        "custom_data",
                        "custom_tag",
                        "scorecard_item",
                        "in_list",
                        "assignment",
                        "description",
                        "custom_list",
                    ].includes(item.key),
            )

        default:
            // exclude custom tags, scorecards, and custom data for everything else
            return items.filter(
                (item) => !["scorecard", "custom_data", "custom_tag", "scorecard_item"].includes(item.key),
            )
    }
}

/**
 * Count the number of bills for each region based on the column data
 * This data will be used to populate the map in sheets
 * @param {List} regionData: a List of region ids of all of the data in the sheet
 * @returns {Array[Object]} An array of objects, where each object is of the form {"state": "ny", value: 0}, for example
 * start with the default mapping that the ReusableMap needs
 */
export const generateMapData = (regionData) => {
    // start with an empty accumulator
    const acc = {}
    try {
        regionData.reduce((acc, region_id) => {
            // if the key is already in the accumulator, just append
            if (region_id in acc) {
                acc[region_id] += 1
            } else {
                // if there is no row_id, then this row_id is the first one
                acc[region_id] = 1
            }
            return acc
        }, acc)

        // now that we have all of the counts, we need to build up the array of objects
        // first, generate a list of all of the state abbreviations along with the region_id
        const stateAbbrevs = Object.entries(DjangIO.app.models.Region._items)
            .filter((intermediate_region_array) => intermediate_region_array[1].is_state_region)
            .map((intermediate_region_array) => ({
                region_id: intermediate_region_array[1].value,
                abbrev: intermediate_region_array[1].abbrev,
            }))

        // map over the state abbreviations, returning an object of the form {"state": "ny", value: 0}
        // for each state
        return stateAbbrevs.map(({ region_id, abbrev }) => ({
            state: abbrev,
            value: acc[region_id] ? acc[region_id] : 0,
        }))
    } catch (e) {
        return []
    }
}
